import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { EMPTY, formatNumber } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';

export interface UsageMeterProps {
  label: string;
  /** null = unknown (never shown as 0). */
  value: number | null;
  /** e.g. "min", "mensajes". */
  unit: string;
  warnAt?: number | null;
  hardCap?: number | null;
  measurement: 'measured' | 'estimated';
  /** e.g. "Septiembre 2026" */
  period?: string;
  /** Custom value formatter (e.g. money). */
  formatValue?: (n: number) => string;
  className?: string;
}

/** Usage against warning threshold and hard cap, labelled as measured or estimated. */
export const UsageMeter = React.forwardRef<HTMLDivElement, UsageMeterProps>(function UsageMeter(
  { label, value, unit, warnAt, hardCap, measurement, period, formatValue, className },
  ref,
) {
  const fmt = (n: number) => (formatValue ? formatValue(n) : `${formatNumber(n)} ${unit}`);
  const max = hardCap ?? (warnAt ? warnAt * 1.25 : null);
  const pct = value !== null && max ? Math.min(100, (value / max) * 100) : null;
  const state = value === null ? 'unknown' : hardCap != null && value >= hardCap ? 'capped' : warnAt != null && value >= warnAt ? 'warning' : 'ok';
  const badge = {
    unknown: <StatusBadge tone="neutral">Sin datos</StatusBadge>,
    ok: <StatusBadge tone="success">Dentro del límite</StatusBadge>,
    warning: <StatusBadge tone="warning">Cerca del límite</StatusBadge>,
    capped: <StatusBadge tone="danger">Límite alcanzado</StatusBadge>,
  }[state];
  const labelId = React.useId();
  const valueText = value === null ? 'Desconocido' : hardCap != null ? `${fmt(value)} de ${fmt(hardCap)}` : fmt(value);
  return (
    <div ref={ref} className={cn('grid gap-2', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span id={labelId} className="text-body font-medium text-text-primary">
          {label}
        </span>
        {badge}
      </div>
      <div
        role="meter"
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={max ?? undefined}
        aria-valuenow={value ?? undefined}
        aria-valuetext={valueText}
        className="relative h-2 w-full overflow-hidden rounded-pill bg-surface-subtle"
      >
        {pct !== null ? (
          <div
            className={cn('h-full rounded-pill', state === 'capped' ? 'bg-danger' : state === 'warning' ? 'bg-warning' : 'bg-brand-primary')}
            style={{ width: `${pct}%` }}
          />
        ) : null}
        {warnAt != null && max ? <span aria-hidden className="absolute inset-y-0 w-0.5 bg-text-secondary" style={{ left: `${Math.min(100, (warnAt / max) * 100)}%` }} /> : null}
      </div>
      <div className="flex flex-wrap justify-between gap-x-3 gap-y-0.5 text-meta text-text-secondary">
        <span className="ef-tabular">
          <span className="font-medium text-text-primary">{value === null ? EMPTY : fmt(value)}</span>
          {hardCap != null ? ` de ${fmt(hardCap)} (límite)` : ''}
          {warnAt != null ? ` · aviso en ${fmt(warnAt)}` : ''}
        </span>
        <span>
          {measurement === 'measured' ? 'Medido' : 'Estimado'}
          {period ? ` · ${period}` : ''}
        </span>
      </div>
    </div>
  );
});
